/**
 * /integration/bindings — what each `@role` means in this environment.
 *
 * A pipeline node can name a role instead of a connection. The graph then says
 * what *kind* of system a step talks to, and this page says *which* — which is
 * what lets one validated pipeline run in staging against staging and in
 * production against production without editing a node.
 *
 * Changing a binding is Administrator-only, and the page says so rather than
 * offering a control that would be refused: it is the difference between a
 * pipeline that writes to the validation vault and one that writes to
 * production.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";

import { useAccessToken } from "@/features/auth/useAccessToken";
import { useEffectiveRoles } from "@/features/auth/RolePreviewContext";
import { useMe } from "@/features/users/useMe";
import {
  listConnectionBindings,
  listConnections,
  removeConnectionBinding,
  setConnectionBinding,
  type BindingOut,
  type ConnectionOut,
} from "@/shared/api/integration";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";

export function ConnectionBindings() {
  const getToken = useAccessToken();
  const { data: me } = useMe();
  // The effective set honours an admin's "view as role" preview, so the page
  // shows a previewing admin exactly what that role would see.
  const isAdmin = useEffectiveRoles(me?.roles ?? []).includes("administrator");

  const [bindings, setBindings] = useState<BindingOut[] | null>(null);
  const [connections, setConnections] = useState<ConnectionOut[]>([]);
  const [editing, setEditing] = useState<{
    role: string;
    connectionId: string;
    note: string;
  } | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      const [bound, conns] = await Promise.all([
        listConnectionBindings(token),
        listConnections(token),
      ]);
      setBindings(bound.items);
      setConnections(conns.items);
    } catch (err) {
      toastApiError("Could not load connection roles", err);
      setBindings([]);
    }
  }, [getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await setConnectionBinding(await getToken(), editing.role, {
        connection_id: editing.connectionId,
        note: editing.note,
      });
      toastSuccess(
        `@${editing.role} bound`,
        "Every pipeline using that role now talks to this connection.",
      );
      setEditing(null);
      await load();
    } catch (err) {
      toastApiError("Could not bind the role", err);
    } finally {
      setSaving(false);
    }
  };

  const unbind = async (role: string) => {
    try {
      await removeConnectionBinding(await getToken(), role);
      toastSuccess(`@${role} unbound`, "Pipelines using it will now refuse to run.");
      await load();
    } catch (err) {
      toastApiError("Could not unbind the role", err);
    }
  };

  return (
    <Stack gap="lg">
      {/* No heading — this renders inside the Integrations hub, whose tab
          already names it. */}
      <Text c="dimmed">
        A pipeline node can name a role — <Code>@source_vault</Code> — instead of a connection. The
        pipeline then says what kind of system a step talks to, and this page says which one it
        means <b>here</b>. That is what lets the same validated pipeline run in staging against
        staging and in production against production without editing a node.
      </Text>

      {!isAdmin && (
        <Alert color="gray" variant="light">
          Only an Administrator can change a binding. It decides whether a pipeline writes to the
          validation vault or the production one, which is not a choice that belongs on a canvas.
        </Alert>
      )}

      {bindings === null ? (
        <Loader />
      ) : bindings.length === 0 ? (
        <Alert color="gray" variant="light">
          No roles bound here yet. A pipeline that uses one will refuse to run until it is bound —
          deliberately: better a refusal naming the role than a run against whatever was nearest.
        </Alert>
      ) : (
        <Card withBorder radius="md" padding={0}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Role</Table.Th>
                <Table.Th>Points at</Table.Th>
                <Table.Th>Note</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {bindings.map((binding) => (
                <Table.Tr key={binding.role}>
                  <Table.Td>
                    <Code>@{binding.role}</Code>
                  </Table.Td>
                  <Table.Td>
                    {binding.connection_name ? (
                      <Group gap="xs">
                        <Text size="sm">{binding.connection_name}</Text>
                        {binding.source_type && (
                          <Badge size="xs" variant="light">
                            {binding.source_type}
                          </Badge>
                        )}
                      </Group>
                    ) : (
                      // A binding can outlive the connection it points at.
                      <Text size="sm" c="orange">
                        that connection no longer exists
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {binding.note || "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end">
                      <Button
                        size="compact-xs"
                        variant="light"
                        disabled={!isAdmin}
                        onClick={() =>
                          setEditing({
                            role: binding.role,
                            connectionId: binding.connection_id,
                            note: binding.note ?? "",
                          })
                        }
                      >
                        Change
                      </Button>
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        color="red"
                        disabled={!isAdmin}
                        leftSection={<IconTrash size={12} />}
                        onClick={() => void unbind(binding.role)}
                      >
                        Unbind
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}

      {isAdmin && (
        <Group>
          <Button
            variant="light"
            onClick={() => setEditing({ role: "", connectionId: "", note: "" })}
          >
            Bind a role
          </Button>
        </Group>
      )}

      <Modal
        opened={editing !== null}
        onClose={() => setEditing(null)}
        title={editing?.role ? `Bind @${editing.role}` : "Bind a role"}
        centered
      >
        <Stack gap="sm">
          <TextInput
            label="Role"
            description="The name pipelines use, without the @. Letters, digits and underscores."
            placeholder="source_vault"
            value={editing?.role ?? ""}
            onChange={(e) =>
              setEditing((prev) => (prev ? { ...prev, role: e.currentTarget.value.trim() } : prev))
            }
          />
          <Select
            label="Connection in this environment"
            placeholder="Pick a connection"
            data={connections.map((c) => ({
              value: c.connection_id,
              label: `${c.display_name} (${c.source_type})`,
            }))}
            value={editing?.connectionId || null}
            onChange={(v) =>
              setEditing((prev) => (prev ? { ...prev, connectionId: v ?? "" } : prev))
            }
            searchable
          />
          <TextInput
            label="Note"
            description="Optional. Why this one — “the validation vault until the 26R2 upgrade”."
            value={editing?.note ?? ""}
            onChange={(e) =>
              setEditing((prev) => (prev ? { ...prev, note: e.currentTarget.value } : prev))
            }
          />
          <Alert color="orange" variant="light">
            Every pipeline using this role will talk to this connection from its next run. The
            change is audited, with the connection the role used to mean.
          </Alert>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              loading={saving}
              disabled={!editing?.role || !editing?.connectionId}
              onClick={() => void save()}
            >
              Bind
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
